<template>
    <div class="base-card__content base-card__zoom base-card__shadow channel-program-content" :class="classesProgramContent">
        <div class="channel-program-content__wrapper">
                <img 
                    v-show="currentProgramImage"
                    loading="lazy"
                    class="channel-program-content__image"
                    :class="{'channel-program-content__image_blured': isParentallyRestricted}"
                    :alt="title"
                    :src="currentProgramImage"
                >
                <common-icon v-show="!currentProgramImage" class="channel-program-content__image" :icon-name="'IconNoPicture'" />

                <div v-show="isRemovedFromFavorite" class="channel-program-content__favorite-label">{{ captionRemoved }}</div>
                <div v-show="isPlaying" class="channel-program-content__label-playing"> <label-playing /></div>
        </div>
        <div class="channel-program-content__logo-overlay">
            <img v-show="imageUrl" :src="imageUrl" :alt="channelName" loading="lazy" class="channel-program-content__channel-logo">
        </div>
        <div class="channel-program-content__pic" />
        <common-icon v-show="isParentallyRestricted" class="channel-program-content__image-lock" :icon-name="'IconLock'" />
        <span class="channel-program-content__track">
            <span :style="{ width: progress + '%' }" />
        </span>
        <div class="base-card__shift">
            <div
                v-show="currentProgram"
                class="channel-program-content__name"
            >
                {{ title }}
            </div>
            <div class="channel-program-content__time">
                {{ timeString }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { type PropType, type Ref, computed, ref, watch, type ComputedRef } from "vue";
    import { type ChannelLiveCardData } from "../../models";
    import { ImgUrlUtils, ProgramUtils, Logger } from "@/utils";
    import { type Channel, type ProgramGuideItem } from "@ca.vmedia.tv/domains";
    import { Strings } from "@/locales/types/enums";
    import { useApplicationStore } from "@/stores/Application";
    import { useChannelsStore } from "@/stores/Channels";
    import { useI18n } from "vue-i18n";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { useFavorite } from "@/composables/useFavorite";
    import { storeToRefs } from 'pinia';
    import { usePlayerStore } from "@/stores/Player";
    import { useProgramInfo } from "@/composables/useProgramInfo/useProgramInfo";
    import { AppConstants} from "@/common";
    import type { AxiosError } from 'axios';

    const PlayerStoreModule = usePlayerStore();
    const {
        playChannel,
        playProgram
    } = storeToRefs(PlayerStoreModule);
    
    const ApplicationStore = useApplicationStore();
    const {
        getCurrentTime: currentTime,
    } = storeToRefs(ApplicationStore);

    const ChannelsStore = useChannelsStore();
    const {
        getAvailableChannels: allChannels,
    } = storeToRefs(ChannelsStore);

    const { hasDeletedIdsFavoriteChannels } = useFavorite();

    const props = defineProps({
        cardData: {
            type: Object as PropType<ChannelLiveCardData>,
            required: true
        },
        /**
         * The flag to indicate if the program info should be enabled
         */
         enableProgramInfo: {
            type: Boolean,
            required: false,
            default: false
        },
    });

    const emit = defineEmits(["program-info"]);
    const { HeaderImageWidth }: typeof AppConstants = AppConstants;
    const { t } = useI18n();
    const { $domains, $services } = useNuxtApp();
    const { needsUnlockPin } = useParentControl()
    const timeString: string = t(Strings.CARD_NOW_PLAYING).toString();
    const captionRemoved: string = t(Strings.CARD_REMOVED).toString();
    const currentProgram: Ref<ProgramGuideItem | null> = ref(null);
    const isLoading: Ref<boolean> = ref(false);
    const imageUrl: Ref<string> = ref('');

    const isRemovedFromFavorite: ComputedRef<boolean> = computed(() => hasDeletedIdsFavoriteChannels(`${props.cardData.getId()}:${props.cardData.getContentType()}`));
    const isPlaying: ComputedRef<boolean> = computed(() => {
        if(playChannel.value) {
            return playChannel.value ? String(playChannel.value.id) === String(props.cardData.getId()) : false;
        } else {
            return playProgram.value && playProgram.value.recentAirTime ? String(playProgram.value.recentAirTime.channelId) === String(props.cardData.getId()) : false;
        }
    });
    const classesProgramContent: ComputedRef<{[key: string]: boolean}> = computed(() => {
        return {
            'channel-program-content_isRemovedFromFavorite': isRemovedFromFavorite.value,
            'channel-program-content_isPlaying': isPlaying.value
        };
    });

    const title = computed((): string => currentProgram.value ? currentProgram.value.name ?? "" : "");
    const channel = computed((): Channel => props.cardData.getChannel());
    const currentProgramDurationMs = computed((): number => {
        if (!currentProgram.value) {
            return 0;
        }

        return currentProgram.value.length! * 60 * 1000;
    });
    const progress = computed((): number => {
        if (!currentProgram.value) {
            return 0;
        }
        
        const total = Math.max(currentProgramDurationMs.value, 1);
        const duration = currentTime.value - currentProgram.value.recentAirTime!.startTime!;
        const progressValue = duration / total;

        return Math.min(1, Math.max(0, progressValue)) * 100;
    });
    const currentProgramImage = computed((): string | undefined => {
        if (!currentProgram.value?.image) {
            return;
        }

        return ImgUrlUtils.src(currentProgram.value.image, 408);
    });
    const isParentallyRestricted = computed((): boolean => needsUnlockPin(currentProgram.value?.advisory ?? currentChannel.value?.advisory));
    const currentChannel = computed((): Channel | null => {
        const channelValue = allChannels.value.find(item => item.id === currentProgram.value?.recentAirTime?.channelId);
        return channelValue ? channelValue : null;
    });
    const channelName = computed((): string => {
        return currentChannel.value && currentChannel.value.name ? currentChannel.value.name : "";
    });


    const fetchProgram = async (): Promise<void> => {
        try {
            isLoading.value = true;
            currentProgram.value = await $services.guide.getChannelProgramByTime(channel.value.id!, currentTime.value);

            imageUrl.value = currentProgram.value && currentProgram.value.recentAirTime 
                ? await $domains.imagesRepository.getWhiteChannelImage(currentProgram.value.recentAirTime.imagesChannel) ?? ''
                : '';
            if (!currentProgram.value) {
                const channelValue = allChannels.value.find(value => value.id === Number(channel.value.id));
                imageUrl.value = channelValue?.image ?? '';
            } 
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        } finally {
            isLoading.value = false;
        }
    };
   
    void fetchProgram();

    watch(
        progress,
        (value: number) => {
            if (value >= 100) {
                const needFetchProgram = !isLoading.value &&
                    currentProgram.value &&
                    !ProgramUtils.isLive(currentProgram.value!);

                if (needFetchProgram) {
                    void fetchProgram();
                }
            }
        }
    );

    if (props.enableProgramInfo) {
        const createProgramInfo = (program: ProgramGuideItem, channelForInfo: Channel | null): void => {
            if(program === null || channelForInfo === null) {
                return;
            }

            const img = ImgUrlUtils.src(program.image, HeaderImageWidth);
            const programInfo = useProgramInfo(program, channelForInfo, img);
            
            emit("program-info", programInfo);
        };

        watch(
            currentProgram,
            async (value: ProgramGuideItem | null) => {
                if (value) {
                    createProgramInfo(value, currentChannel.value);
                }
            }
        );
    }
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
